/**
 * Where the realtime plane lives.
 *
 * The spheromak listener is a **second port** (ADR 0004) — axum serves HTTP and
 * the SPA, spheromak serves the game. In development the vite proxy forwards
 * `/rpc` to it with `ws: true`; in production nginx does the same. Either way the
 * page talks to one origin and the scheme follows it, so a site on https gets
 * `wss` without anybody remembering to change a constant.
 *
 * Derived rather than configured for exactly that reason: a build-time URL is a
 * value that is right on one machine and wrong on the next, and the failure is a
 * socket that never opens with nothing useful in the console.
 *
 * **That reasoning is still correct, and `serverOrigin()` is where it now
 * lives** — because a packaged television app (phase 11) has no origin to derive
 * from, and it is not only this URL that breaks when it does not. Note in
 * particular that the generated client derives the **session lock's** address
 * from this one (`httpOrigin()` in `generated/client.ts`), so a wrong host here
 * is also phase 9's silent-plaintext-downgrade path.
 */

import { serverOrigin, type PageLocation } from "./serverOrigin.js";

/** The websocket URL for the realtime plane. */
export function realtimeUrl(page: PageLocation = window.location): string {
  const origin = new URL(serverOrigin(page));
  const scheme = origin.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${origin.host}/rpc`;
}
